/**
 * VC Product Wishlist Button
 * Stores richer product metadata and keeps the saved item aligned with the current variant.
 */
(function() {
  'use strict';

  const STORAGE_PREFIX = 'vc_wishlist_customer';

  function getCustomerId() {
    const rawCustomerId = window.vcWishlistConfig?.customerId;
    if (rawCustomerId === null || rawCustomerId === undefined) return '';

    const normalizedCustomerId = String(rawCustomerId).trim();
    if (!normalizedCustomerId || normalizedCustomerId === 'null' || normalizedCustomerId === 'undefined') {
      return '';
    }

    return normalizedCustomerId;
  }

  function isLoggedIn() {
    return getCustomerId() !== '';
  }

  function getStorageKey() {
    const customerId = getCustomerId();
    return customerId ? `${STORAGE_PREFIX}_${customerId}` : null;
  }

  function readWishlist() {
    const storageKey = getStorageKey();
    if (!storageKey) return [];

    try {
      return normalizeWishlistItems(JSON.parse(localStorage.getItem(storageKey) || '[]'));
    } catch (error) {
      // Error handled silently in production - wishlist parse failed
      return [];
    }
  }

  function normalizeWishlistItems(items) {
    if (!Array.isArray(items)) return [];

    const normalized = items
      .map((item) => {
        if (typeof item === 'string') {
          return {
            handle: item,
            productId: '',
            title: '',
            url: '',
            productUrl: '',
            image: '',
            price: '',
            compareAtPrice: '',
            variantId: '',
            variantTitle: '',
            addedAt: '',
          };
        }

        if (!item || typeof item !== 'object') return null;

        return {
          handle: String(item.handle || ''),
          productId: String(item.productId || ''),
          title: String(item.title || ''),
          url: String(item.url || item.productUrl || ''),
          productUrl: String(item.productUrl || item.url || ''),
          image: String(item.image || ''),
          price: String(item.price || ''),
          compareAtPrice: String(item.compareAtPrice || ''),
          variantId: String(item.variantId || ''),
          variantTitle: String(item.variantTitle || ''),
          addedAt: String(item.addedAt || ''),
        };
      })
      .filter(Boolean);

    const deduped = [];
    const seen = new Set();

    normalized.forEach((item) => {
      const key = `${item.productId || ''}::${item.handle || ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      deduped.push(item);
    });

    return deduped;
  }

  function writeWishlist(items, detail = {}) {
    const storageKey = getStorageKey();
    if (!storageKey) return [];

    const normalizedItems = normalizeWishlistItems(items);
    let writeSucceeded = true;

    try {
      localStorage.setItem(storageKey, JSON.stringify(normalizedItems));
    } catch (error) {
      // localStorage quota exceeded or unavailable
      writeSucceeded = false;
    }

    if (writeSucceeded) {
      window.dispatchEvent(new CustomEvent('vc:wishlist:updated', {
        detail: {
          wishlist: normalizedItems,
          count: normalizedItems.length,
          handles: normalizedItems.map((item) => item.handle).filter(Boolean),
          ...detail,
        },
      }));
    }

    return normalizedItems;
  }

  function formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    const amount = (cents / 100).toFixed(2);
    const currencySymbol = window.Shopify?.currency?.symbol || '$';
    return `${currencySymbol}${amount}`;
  }

  function buildVariantUrl(productUrl, variantId) {
    const url = new URL(productUrl || window.location.pathname, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }
    return url.toString();
  }

  function ensureAbsoluteUrl(url) {
    if (!url) return '';
    if (url.startsWith('//')) return `${window.location.protocol}${url}`;

    try {
      return new URL(url, window.location.origin).toString();
    } catch (error) {
      return url;
    }
  }

  function normalizeVariantTitle(title) {
    const normalized = String(title || '').trim();
    return normalized && normalized !== 'Default Title' ? normalized : '';
  }

  function extractVariantImage(variant) {
    if (!variant) return '';

    return variant.featured_image?.src ||
      variant.featured_image?.url ||
      variant.featured_media?.preview_image?.src ||
      variant.featured_media?.preview_image?.url ||
      '';
  }

  if (!customElements.get('vc-wishlist-button')) {
    customElements.define(
      'vc-wishlist-button',
      class VCWishlistButton extends HTMLElement {
        constructor() {
          super();

          this.button = this.querySelector('.vc-product__wishlist-heart');
          this.textEl = this.querySelector('.vc-product__wishlist-text');
          this.statusEl = this.querySelector('.vc-product__wishlist-status');
          this.sectionId = String(this.dataset.sectionId || '');
          this.productHandle = String(this.dataset.productHandle || '');
          this.productId = String(this.dataset.productId || '');
          this.defaultText = this.dataset.defaultText || 'Save to Project';
          this.savedText = this.dataset.savedText || 'Saved to Project';
          this.addLabel = this.dataset.addLabel || 'Add to wishlist';
          this.removeLabel = this.dataset.removeLabel || 'Remove from wishlist';
          this.addedMessage = this.dataset.addedMessage || 'Added to wishlist';
          this.removedMessage = this.dataset.removedMessage || 'Removed from wishlist';
          this.loginRequiredMessage = this.dataset.loginRequiredMessage || 'Please sign in to save items';
          this.loginUrl = this.dataset.loginUrl || window.vcWishlistConfig?.loginUrl || '/account/login';

          this.handleToggle = this.handleToggle.bind(this);
          this.handleWishlistUpdate = this.handleWishlistUpdate.bind(this);
          this.handleStorageSync = this.handleStorageSync.bind(this);
          this.handleVariantChangeEvent = this.handleVariantChangeEvent.bind(this);
          this.handleVariantChangePubSub = this.handleVariantChangePubSub.bind(this);
        }

        connectedCallback() {
          if (!this.button) return;

          this.syncButtonState();
          this.button.addEventListener('click', this.handleToggle);
          window.addEventListener('vc:wishlist:updated', this.handleWishlistUpdate);
          window.addEventListener('storage', this.handleStorageSync);
          document.addEventListener('variant:changed', this.handleVariantChangeEvent);

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChangePubSub);
          }
        }

        disconnectedCallback() {
          this.button?.removeEventListener('click', this.handleToggle);
          window.removeEventListener('vc:wishlist:updated', this.handleWishlistUpdate);
          window.removeEventListener('storage', this.handleStorageSync);
          document.removeEventListener('variant:changed', this.handleVariantChangeEvent);
          this.variantChangeUnsubscriber?.();
        }

        getSectionRoot() {
          return document.getElementById(`MainProduct-${this.sectionId}`) || this.closest('product-info') || document;
        }

        getCurrentImage(sectionRoot = this.getSectionRoot()) {
          const image =
            sectionRoot?.querySelector('.vc-product__thumbnail--active img') ||
            sectionRoot?.querySelector('.vc-product__media img') ||
            sectionRoot?.querySelector('.vc-main-media-image') ||
            document.querySelector('meta[property="og:image"]');

          if (!image) return '';
          return image.getAttribute?.('content') || image.currentSrc || image.src || '';
        }

        getCurrentText(sectionRoot, selectors) {
          const element = sectionRoot?.querySelector(selectors);
          return element?.textContent?.trim() || '';
        }

        getWishlist() {
          return readWishlist();
        }

        findItemIndex(items = this.getWishlist()) {
          return items.findIndex((item) => {
            const sameHandle = this.productHandle && item.handle === this.productHandle;
            const sameProductId = this.productId && item.productId === this.productId;
            return sameHandle || sameProductId;
          });
        }

        isInWishlist() {
          return this.findItemIndex() > -1;
        }

        getCurrentWishlistItem() {
          const sectionRoot = this.getSectionRoot();
          const variantId = String(this.dataset.variantId || sectionRoot?.querySelector('input[name="id"]')?.value || '');
          const variantTitle = normalizeVariantTitle(this.dataset.variantTitle || '');
          const productUrl = this.dataset.productUrl || sectionRoot?.dataset.url || window.location.pathname;
          const image = ensureAbsoluteUrl(this.dataset.productImage || this.getCurrentImage(sectionRoot) || '');
          const price = this.dataset.productPrice || this.getCurrentText(sectionRoot, '.vc-product__price-wrapper .price__sale .price-item--sale, .vc-product__price-wrapper .price-item--sale, .vc-product__price-wrapper .price-item--regular');
          const compareAtPrice = this.dataset.productCompareAtPrice || this.getCurrentText(sectionRoot, '.vc-product__price-wrapper .price__sale .price-item--regular');

          return {
            handle: this.productHandle,
            productId: this.productId,
            title: this.dataset.productTitle || document.title,
            url: buildVariantUrl(productUrl, variantId),
            productUrl: buildVariantUrl(productUrl, variantId),
            image,
            price,
            compareAtPrice,
            variantId,
            variantTitle,
            addedAt: new Date().toISOString(),
          };
        }

        setButtonState(isActive, announceMessage = '') {
          if (!this.button) return;

          this.button.classList.toggle('is-active', isActive);
          this.button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          this.button.setAttribute('aria-label', isActive ? this.removeLabel : this.addLabel);
          this.button.setAttribute('title', isActive ? this.removeLabel : this.addLabel);

          if (this.textEl) {
            this.textEl.textContent = isActive ? this.savedText : this.defaultText;
          }

          if (this.statusEl) {
            this.statusEl.textContent = announceMessage;
          }
        }

        syncButtonState() {
          if (!isLoggedIn()) {
            this.setButtonState(false);
            return;
          }

          this.setButtonState(this.isInWishlist());
        }

        handleToggle(event) {
          event.preventDefault();

          if (!isLoggedIn()) {
            this.setButtonState(false, this.loginRequiredMessage);
            if (this.loginUrl) {
              window.location.assign(this.loginUrl);
            }
            return;
          }

          const items = this.getWishlist();
          const existingIndex = this.findItemIndex(items);

          if (existingIndex > -1) {
            const [removedItem] = items.splice(existingIndex, 1);
            writeWishlist(items, {
              action: 'removed',
              productHandle: this.productHandle,
              item: removedItem || null,
            });
            this.setButtonState(false, this.removedMessage);
            return;
          }

          const newItem = this.getCurrentWishlistItem();
          items.push(newItem);
          writeWishlist(items, {
            action: 'added',
            productHandle: this.productHandle,
            item: newItem,
          });
          this.setButtonState(true, this.addedMessage);
        }

        syncVariantData(variant) {
          if (!variant) return;

          this.dataset.variantId = variant.id ? String(variant.id) : '';
          this.dataset.variantTitle = normalizeVariantTitle(variant.title);

          if (typeof variant.price === 'number') {
            this.dataset.productPrice = formatMoney(variant.price);
          }

          if (typeof variant.compare_at_price === 'number' && variant.compare_at_price > variant.price) {
            this.dataset.productCompareAtPrice = formatMoney(variant.compare_at_price);
          } else {
            this.dataset.productCompareAtPrice = '';
          }

          const image = ensureAbsoluteUrl(extractVariantImage(variant) || this.getCurrentImage(this.getSectionRoot()) || '');
          if (image) {
            this.dataset.productImage = image;
          }

          const items = this.getWishlist();
          const existingIndex = this.findItemIndex(items);
          if (existingIndex > -1) {
            const updatedItem = {
              ...items[existingIndex],
              ...this.getCurrentWishlistItem(),
              addedAt: items[existingIndex].addedAt || new Date().toISOString(),
            };
            items.splice(existingIndex, 1, updatedItem);
            writeWishlist(items, {
              action: 'updated',
              productHandle: this.productHandle,
              item: updatedItem,
            });
          } else {
            this.syncButtonState();
          }
        }

        handleVariantChangePubSub(event) {
          const sectionId = String(event?.data?.sectionId || '');
          if (this.sectionId && sectionId && this.sectionId !== sectionId) return;

          this.syncVariantData(event?.data?.variant || null);
        }

        handleVariantChangeEvent(event) {
          const sectionId = String(event.target?.closest?.('variant-selector')?.dataset.section || '');
          if (this.sectionId && sectionId && this.sectionId !== sectionId) return;

          this.syncVariantData(event.detail?.variant || null);
        }

        handleWishlistUpdate(event) {
          const handles = Array.isArray(event.detail?.handles) ? event.detail.handles : [];
          const isActive = handles.includes(this.productHandle) || this.isInWishlist();

          if (event.detail?.productHandle === this.productHandle) {
            const message = event.detail.action === 'removed'
              ? this.removedMessage
              : event.detail.action === 'added'
                ? this.addedMessage
                : '';
            this.setButtonState(isActive, message);
            return;
          }

          this.setButtonState(isActive);
        }

        handleStorageSync(event) {
          if (!event.key || !event.key.startsWith(STORAGE_PREFIX)) return;
          this.syncButtonState();
        }

        static getWishlistItems() {
          return readWishlist();
        }

        static getWishlistCount() {
          return readWishlist().length;
        }
      }
    );
  }

  window.VCWishlist = {
    getItems: readWishlist,
    getCount: () => readWishlist().length,
    isLoggedIn,
    getStorageKey,
    setItems(items, detail = {}) {
      return writeWishlist(items, detail);
    },
    remove(productId, handle) {
      const normalizedProductId = String(productId || '');
      const normalizedHandle = String(handle || '');
      const items = readWishlist();
      const existingIndex = items.findIndex((item) => {
        const sameHandle = normalizedHandle && item.handle === normalizedHandle;
        const sameProductId = normalizedProductId && item.productId === normalizedProductId;
        return sameHandle || sameProductId;
      });

      if (existingIndex === -1) return items;

      const [removedItem] = items.splice(existingIndex, 1);
      return writeWishlist(items, {
        action: 'removed',
        productHandle: normalizedHandle || removedItem?.handle || '',
        item: removedItem || null,
      });
    },
    clear() {
      writeWishlist([], { action: 'cleared', productHandle: '', item: null });
    },
  };
})();
